// Class schedule tab (web parity for the iOS ClanRulesView "Schedule" section):
// class-scoped homework / blocks / tasks pushed to every member, shown in an
// interactive month grid (default) or a grouped list. Admins (owner/TA) can
// add/edit/delete; members see read-only. Backend enforces the role gate on
// POST/PATCH/DELETE /calendar with clan_id.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  type CalendarEntry,
  type CalendarEntryKind,
  deleteCalendarEntry,
  listCalendar,
} from "@/api/calendar";
import { Button, Spinner, cx } from "@/components/ui";
import { EntryForm, type EntrySeed, scheduleText } from "@/calendar/entry-form";
import { CalendarGrid, type GridSeed } from "@/calendar/calendar-grid";
import { KIND_ICON } from "@/calendar/calendar-icons";

type FormState = { mode: "create"; seed?: EntrySeed } | { mode: "edit"; entry: CalendarEntry } | null;

export function SchedulePanel({ clanId, canManage }: { clanId: string; canManage: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [view, setView] = useState<"calendar" | "list">("calendar");
  const [form, setForm] = useState<FormState>(null);

  const entriesQuery = useQuery({ queryKey: ["calendar"], queryFn: listCalendar });
  const entries = (entriesQuery.data ?? []).filter((e) => e.clan_id === clanId);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["calendar"] });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteCalendarEntry(id),
    onSuccess: invalidate,
  });

  const groups: { key: CalendarEntryKind; label: string }[] = [
    { key: "homework", label: t("Homework") },
    { key: "block", label: t("Blocks") },
    { key: "task", label: t("Tasks") },
  ];

  const openCreate = (seed?: GridSeed) => canManage && setForm({ mode: "create", seed });
  const openEdit = (entry: CalendarEntry) => setForm({ mode: "edit", entry });
  const closeForm = () => setForm(null);

  if (entriesQuery.isLoading) {
    return (
      <div className="flex justify-center py-12 text-brand-600">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="inline-flex rounded-xl bg-black/5 p-1">
          {(["calendar", "list"] as const).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={cx(
                "rounded-lg px-3 py-1 text-sm font-semibold capitalize transition",
                view === v ? "bg-white text-brand-700 shadow-soft" : "text-ink-faint",
              )}
            >
              {v === "calendar" ? t("Calendar") : t("List")}
            </button>
          ))}
        </div>
        {canManage && !form && (
          <Button size="sm" variant="secondary" onClick={() => openCreate()}>
            {t("Add entry")}
          </Button>
        )}
      </div>

      {form && (
        <EntryForm
          clanId={clanId}
          initial={form.mode === "edit" ? form.entry : undefined}
          seed={form.mode === "create" ? form.seed : undefined}
          readOnly={!canManage}
          onDone={() => { closeForm(); invalidate(); }}
          onCancel={closeForm}
        />
      )}

      {view === "calendar" && (
        <CalendarGrid
          entries={entries}
          canEdit={canManage}
          onCreate={openCreate}
          onEdit={openEdit}
        />
      )}

      {view === "list" && (
        <>
          {entries.length === 0 && !form && (
            <p className="py-6 text-center text-sm text-ink-faint">
              {canManage
                ? t("Add a blocking window, homework, or task for the whole class.")
                : t("Nothing scheduled for this class yet.")}
            </p>
          )}
          {groups.map(({ key, label }) => {
            const items = entries.filter((e) => e.entry_kind === key);
            if (items.length === 0) return null;
            return (
              <div key={key}>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-faint">{label}</h2>
                <div className="mt-2 space-y-2">
                  {items.map((e) => (
                    <div key={e.id} className="card flex items-center gap-3 p-3.5">
                      <span className="text-lg" aria-hidden>{KIND_ICON[e.entry_kind]}</span>
                      <button className="min-w-0 flex-1 text-left" onClick={() => openEdit(e)}>
                        <span className="block truncate font-medium text-ink">{e.title}</span>
                        {scheduleText(e) && <p className="truncate text-xs text-ink-faint">{scheduleText(e)}</p>}
                        {e.question_set_titles.length > 0 && (
                          <p className="truncate text-xs text-ink-faint">{e.question_set_titles.join(", ")}</p>
                        )}
                      </button>
                      {canManage && (
                        <button
                          className="shrink-0 text-xs text-red-600 hover:underline"
                          onClick={() => deleteMut.mutate(e.id)}
                        >
                          {t("Delete")}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
